import { useState } from "react";
import { Pencil } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { http, errMsg, FAQ_CATS } from "@/lib/api";
import { F, inputCls, StatusPill, ConfirmDelete, MoveButtons, moveItem, PublishControls, FormButtons, saveItem, ListToolbar, matches, PanelHead, AddButton } from "@/components/admin/shared";

const blank = { question: "", answer: "", category: "umum", enabled: true };

const FaqForm = ({ initial, onSaved, onClose }) => {
  const [f, setF] = useState(initial);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const save = async (e, publish = false) => {
    e?.preventDefault();
    setBusy(true);
    try {
      await saveItem("faqs", f, { question: f.question, answer: f.answer, category: f.category, enabled: f.enabled }, publish, (d) => setF((x) => ({ ...x, id: d.id })));
      toast.success(publish ? "FAQ disimpan & diterbitkan" : "Draft FAQ disimpan");
      onSaved();
      onClose();
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={save} className="grid sm:grid-cols-2 gap-3" data-testid="faq-form">
      <F label="Pertanyaan *" className="sm:col-span-2"><input required minLength={3} maxLength={200} className={inputCls} value={f.question} onChange={set("question")} data-testid="faq-question-input" /></F>
      <F label="Jawaban *" hint="Tulis hanya jawaban yang sudah diverifikasi. Hindari saran medis." className="sm:col-span-2"><textarea required maxLength={1500} rows={5} className={inputCls} value={f.answer} onChange={set("answer")} data-testid="faq-answer-input" /></F>
      <F label="Kategori"><select className={inputCls} value={f.category} onChange={set("category")} data-testid="faq-category-select">{Object.entries(FAQ_CATS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select></F>
      <label className="flex items-center gap-2 text-sm text-nanina-green self-end pb-2"><Switch checked={f.enabled} onCheckedChange={(v) => setF({ ...f, enabled: v })} data-testid="faq-enabled-switch" /> Aktif ditampilkan</label>
      <FormButtons busy={busy} onPublish={() => save(null, true)} testId="faq" />
    </form>
  );
};

export const FaqPanel = ({ faqs, reload }) => {
  const [editing, setEditing] = useState(null);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [cat, setCat] = useState("");
  const remove = async (f) => {
    try { await http.delete(`/admin/faqs/${f.id}`); toast.success("FAQ dihapus"); reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  const move = async (i, d) => { if (await moveItem(faqs, i, d, "/admin/faqs/reorder")) reload(); };
  const toggle = async (f, enabled) => {
    try {
      await http.put(`/admin/faqs/${f.id}`, { question: f.question, answer: f.answer, category: f.category, enabled });
      if (f.status === "published") await http.post(`/admin/content/faqs/${f.id}/publish`);
      toast.success(enabled ? "FAQ diaktifkan" : "FAQ dinonaktifkan");
      reload();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const filtering = q || status || cat;
  const list = faqs.map((f, i) => ({ f, i })).filter(({ f }) => matches(f, q, status, ["question", "answer"]) && (!cat || f.category === cat));
  return (
    <div data-testid="faq-panel">
      <PanelHead text="Kelola pertanyaan umum. Hanya FAQ yang aktif dan terbit yang tampil di website." action={<AddButton onClick={() => setEditing({ ...blank })} label="Tambah FAQ" testId="faq-add-button" />} />
      <ListToolbar q={q} setQ={setQ} status={status} setStatus={setStatus} testId="faq">
        <select value={cat} onChange={(e) => setCat(e.target.value)} className={`${inputCls} !mt-0 sm:w-40`} data-testid="faq-category-filter">
          <option value="">Semua kategori</option>
          {Object.entries(FAQ_CATS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
      </ListToolbar>
      {list.length === 0 && <p className="rounded-2xl border border-dashed border-nanina-line p-6 text-center text-sm text-nanina-muted" data-testid="faq-empty">{faqs.length ? "Tidak ada FAQ yang cocok." : "Belum ada FAQ. Bagian FAQ tersembunyi di website."}</p>}
      <ul className="space-y-2">
        {list.map(({ f, i }) => (
          <li key={f.id} className="flex flex-wrap items-center gap-3 rounded-2xl bg-white border border-nanina-line p-3" data-testid={`admin-faq-${f.id}`}>
            <div className="min-w-0 flex-1 basis-full sm:basis-0">
              <p className="text-sm font-semibold text-nanina-green">{f.question}</p>
              <p className="text-xs text-nanina-muted line-clamp-1">{f.answer}</p>
              <span className="inline-flex flex-wrap gap-1 mt-1"><StatusPill item={f} /><span className="text-[10px] rounded-full bg-nanina-skysoft px-2 py-0.5 text-nanina-green">{FAQ_CATS[f.category]}</span>{!f.enabled && <span className="text-[10px] rounded-full bg-gray-100 px-2 py-0.5 text-gray-600">Nonaktif</span>}</span>
            </div>
            <Switch checked={!!f.enabled} onCheckedChange={(v) => toggle(f, v)} aria-label="Aktif" data-testid={`faq-toggle-${f.id}`} />
            <PublishControls item={f} coll="faqs" reload={reload} testId={`faq-${f.id}`} />
            {!filtering && <MoveButtons onUp={() => move(i, -1)} onDown={() => move(i, 1)} testId={`faq-move-${f.id}`} />}
            <button onClick={() => setEditing({ ...f })} className="p-2 rounded-lg hover:bg-nanina-bg" aria-label="Edit" data-testid={`faq-edit-${f.id}`}><Pencil className="h-4 w-4" /></button>
            <ConfirmDelete label={f.question} onConfirm={() => remove(f)} testId={`faq-delete-${f.id}`} />
          </li>
        ))}
      </ul>
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing?.id ? "Edit FAQ" : "Tambah FAQ"}</DialogTitle><DialogDescription>Jangan menulis jadwal, kebijakan, atau saran medis yang belum diverifikasi.</DialogDescription></DialogHeader>
          {editing && <FaqForm initial={editing} onSaved={reload} onClose={() => setEditing(null)} />}
        </DialogContent>
      </Dialog>
    </div>
  );
};
